import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Divider,
  FormControlLabel,
  Radio,
  RadioGroup,
  Container,
  Fade,
  Avatar,
  Tooltip,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CardHeader,
  Badge,
} from '@mui/material';
import {
  Assessment as ReportsIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  AttachMoney as MoneyIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
  Print as PrintIcon,
  CalendarToday as CalendarIcon,
  Business as CompanyIcon,
  ExpandMore as ExpandMoreIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import * as XLSX from 'xlsx';
import { payrollService } from '../services/payrollService';
import { employeeService } from '../services/employeeService';
import { Payroll, Employee } from '../types';
import { formatDate } from '../utils/dateUtils';
import { formatCurrency } from '../utils/currencyutils';

const ReportsPage: React.FC = () => {
  const [payrolls, setPayrolls] = useState<Payroll[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const [selectedMonth, setSelectedMonth] = useState<string>('');
  const [selectedYear, setSelectedYear] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [filteredPayrolls, setFilteredPayrolls] = useState<Payroll[]>([]);
  const [filterType, setFilterType] = useState<'month' | 'date'>('month');
  const [expandedEmployees, setExpandedEmployees] = useState<Set<string>>(new Set());

  const reportRef = useRef<HTMLDivElement>(null);

  

  // Generate month and year options
  const months = [
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' },
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [payrollData, employeeData] = await Promise.all([
          payrollService.getAll(),
          employeeService.getAll()
        ]);
        setPayrolls(payrollData);
        setEmployees(employeeData);
        setFilteredPayrolls(payrollData);
        
        // Set default to current month and year
        const now = new Date();
        const currentMonth = (now.getMonth() + 1).toString().padStart(2, '0');
        const currentYearStr = now.getFullYear().toString();
        setSelectedMonth(currentMonth);
        setSelectedYear(currentYearStr);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    // Filter payrolls based on filter type
    let filtered = payrolls;
    
    if (filterType === 'month') {
      if (selectedMonth && selectedYear) {
        filtered = payrolls.filter(payroll => {
          // Validate payment_date before creating Date object
          if (!payroll.payment_date) return false;
          
          const payrollDate = new Date(payroll.payment_date);
          // Check if date is valid
          if (isNaN(payrollDate.getTime())) return false;
          
          const payrollMonth = (payrollDate.getMonth() + 1).toString().padStart(2, '0');
          const payrollYear = payrollDate.getFullYear().toString();
          return payrollMonth === selectedMonth && payrollYear === selectedYear;
        });
      }
      // If no month/year selected, show all payrolls
    } else if (filterType === 'date') {
      if (startDate && endDate) {
        filtered = payrolls.filter(payroll => {
          // Validate payment_date before comparison
          if (!payroll.payment_date) return false;
          
          const payrollDate = payroll.payment_date;
          return payrollDate >= startDate && payrollDate <= endDate;
        });
      } else {
        // If no date range selected, show empty results
        filtered = [];
      }
    }
    
    setFilteredPayrolls(filtered);
  }, [selectedMonth, selectedYear, startDate, endDate, filterType, payrolls]);

  const handleApplyDateFilter = () => {
    // Trigger re-filtering when apply button is clicked
    if (filterType === 'date' && startDate && endDate) {
      const filtered = payrolls.filter(payroll => {
        // Validate payment_date before comparison
        if (!payroll.payment_date) return false;
        
        const payrollDate = payroll.payment_date;
        return payrollDate >= startDate && payrollDate <= endDate;
      });
      setFilteredPayrolls(filtered);
    }
  };

  const handleFilterTypeChange = (newFilterType: 'month' | 'date') => {
    setFilterType(newFilterType);
    // Clear filters when switching filter type
    if (newFilterType === 'month') {
      setStartDate('');
      setEndDate('');
    } else {
      setSelectedMonth('');
      setSelectedYear('');
    }
  };

  const handleClearFilters = () => {
    setSelectedMonth('');
    setSelectedYear('');
    setStartDate('');
    setEndDate('');
    setFilteredPayrolls(payrolls);
  };



  const getEmployeeName = (employeeId: string) => {
    const employee = employees.find(emp => emp.id === employeeId);
    return employee ? `${employee.first_name} ${employee.last_name}` : 'Unknown Employee';
  };

  const exportToPDF = async () => {
    if (!reportRef.current) return;
    
    setExporting(true);
    try {
      const canvas = await html2canvas(reportRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('l', 'mm', 'a4');
      const imgWidth = 297;
      const pageHeight = 210;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      
      let position = 0;
      
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      
      const fileName = `Payroll_Report_${filterType === 'month' ? `${selectedMonth}_${selectedYear}` : `${startDate}_to_${endDate}`}.pdf`;
      pdf.save(fileName);
    } catch (error) {
      console.error('Error exporting to PDF:', error);
    } finally {
      setExporting(false);
    }
  };

  const exportToExcel = () => {
    setExporting(true);
    try {
      const exportData = filteredPayrolls.map(payroll => ({
        'Employee Name': getEmployeeName(payroll.employee_id),
        'Pay Period': `${payroll.pay_period_start ? formatDate(payroll.pay_period_start) : 'N/A'} - ${payroll.pay_period_end ? formatDate(payroll.pay_period_end) : 'N/A'}`,
        'Basic Salary': payroll.basic_salary,
        'Overtime Pay': payroll.overtime_pay,
        'Bonuses': payroll.bonuses,
        'Gross Pay': payroll.gross_pay,
        'SSS Contribution': payroll.sss_contribution,
        'PhilHealth Contribution': payroll.philhealth_contribution,
        'Pag-IBIG Contribution': payroll.pagibig_contribution,
        'Withholding Tax': payroll.withholding_tax,
        'Other Deductions': payroll.other_deductions,
        'Total Deductions': payroll.total_deductions,
        'Net Pay': payroll.net_pay,
        'Payment Status': payroll.payment_status,
        'Payment Date': payroll.payment_date ? formatDate(payroll.payment_date) : 'N/A'
      }));
      
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Payroll Report');
      
      const fileName = `Payroll_Report_${filterType === 'month' ? `${selectedMonth}_${selectedYear}` : `${startDate}_to_${endDate}`}.xlsx`;
      XLSX.writeFile(wb, fileName);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
    } finally {
      setExporting(false);
    }
  };

  const printReport = () => {
    window.print();
  };

  // Calculate summary statistics
  const totalGrossPay = filteredPayrolls.reduce((sum, p) => sum + p.gross_pay, 0);
  const totalNetPay = filteredPayrolls.reduce((sum, p) => sum + p.net_pay, 0);
  const totalDeductions = filteredPayrolls.reduce((sum, p) => sum + p.total_deductions, 0);
  const totalEmployees = new Set(filteredPayrolls.map(p => p.employee_id)).size;



  // Handle accordion expansion
  const handleAccordionChange = (employeeId: string) => {
    const newExpanded = new Set(expandedEmployees);
    if (newExpanded.has(employeeId)) {
      newExpanded.delete(employeeId);
    } else {
      newExpanded.add(employeeId);
    }
    setExpandedEmployees(newExpanded);
  };

  // Group payroll records by employee
  const groupPayrollsByEmployee = () => {
    const grouped: { [key: string]: { employee: Employee; payrolls: Payroll[] } } = {};
    
    filteredPayrolls.forEach(payroll => {
      const employee = employees.find(emp => emp.id === payroll.employee_id);
      if (employee) {
        if (!grouped[payroll.employee_id]) {
          grouped[payroll.employee_id] = {
            employee,
            payrolls: []
          };
        }
        grouped[payroll.employee_id].payrolls.push(payroll);
      }
    });
    
    // Sort payrolls within each group by payment date (newest first)
    Object.values(grouped).forEach(group => {
      group.payrolls.sort((a, b) => {
        const dateA = a.payment_date ? new Date(a.payment_date).getTime() : 0;
        const dateB = b.payment_date ? new Date(b.payment_date).getTime() : 0;
        return dateB - dateA;
      });
    });
    
    return grouped;
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Fade in={loading}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '60vh',
            gap: 2
          }}>
            <CircularProgress size={60} thickness={4} sx={{ color: '#2563eb' }} />
            <Typography variant="h6" color="text.secondary">
              Loading reports...
            </Typography>
          </Box>
        </Fade>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header Section */}
      <Card
        sx={{
          background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 50%, #1e40af 100%)',
          color: 'white',
          mb: 4,
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 20px 40px rgba(37, 99, 235, 0.2), 0 8px 16px rgba(124, 58, 237, 0.15)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(45deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)',
            opacity: 1,
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.08"%3E%3Ccircle cx="30" cy="30" r="4"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            opacity: 0.4,
          },
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Stack direction="row" spacing={3} alignItems="center">
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  background: 'rgba(255, 255, 255, 0.2)',
                  backdropFilter: 'blur(10px)',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                }}
              >
                <ReportsIcon sx={{ fontSize: 40, color: 'white' }} />
              </Avatar>
              <Box>
                <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
                  Payroll Reports
                </Typography>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', color: 'rgba(255, 255, 255, 0.9)' }}>
                  Comprehensive payroll analysis and reporting dashboard
                </Typography>
              </Box>
            </Stack>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CompanyIcon sx={{ fontSize: 24 }} />
              <Typography variant="h6">PayrollPro System</Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Filter Controls */}
      <Card sx={{ overflow: 'hidden', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)', mb: 4 }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar sx={{ width: 48, height: 48, backgroundColor: '#2563eb' }}>
                <CalendarIcon sx={{ color: 'white' }} />
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#374151' }}>
                  Filter & Export Options
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Configure report parameters and export data
                </Typography>
              </Box>
            </Stack>
            
            {/* Export Buttons */}
            <Stack direction="row" spacing={1}>
              <Tooltip title="Export to PDF">
                <Button
                  variant="outlined"
                  startIcon={<PdfIcon />}
                  onClick={exportToPDF}
                  disabled={exporting || filteredPayrolls.length === 0}
                  sx={{ 
                    borderRadius: 2,
                    '&:hover': {
                      backgroundColor: 'rgba(37, 99, 235, 0.1)',
                    },
                  }}
                >
                  PDF
                </Button>
              </Tooltip>
              <Tooltip title="Export to Excel">
                <Button
                  variant="outlined"
                  startIcon={<ExcelIcon />}
                  onClick={exportToExcel}
                  disabled={exporting || filteredPayrolls.length === 0}
                  sx={{ 
                    borderRadius: 2,
                    '&:hover': {
                      backgroundColor: 'rgba(16, 185, 129, 0.1)',
                    },
                  }}
                >
                  Excel
                </Button>
              </Tooltip>
              <Tooltip title="Print Report">
                <Button
                  variant="outlined"
                  startIcon={<PrintIcon />}
                  onClick={printReport}
                  disabled={filteredPayrolls.length === 0}
                  sx={{ 
                    borderRadius: 2,
                    '&:hover': {
                      backgroundColor: 'rgba(245, 158, 11, 0.1)',
                    },
                  }}
                >
                  Print
                </Button>
              </Tooltip>
            </Stack>
          </Box>
        
          <Divider sx={{ mb: 3 }} />
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            <Box sx={{ flex: '1 1 400px', minWidth: '300px' }}>
              <Card sx={{ p: 3, backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                <FormControl component="fieldset">
                  <RadioGroup
                    row
                    value={filterType}
                    onChange={(e) => handleFilterTypeChange(e.target.value as 'month' | 'date')}
                  >
                    <FormControlLabel 
                      value="month" 
                      control={<Radio />} 
                      label="Filter by Month/Year" 
                      sx={{ mr: 4 }}
                    />
                    <FormControlLabel 
                      value="date" 
                      control={<Radio />} 
                      label="Filter by Date Range" 
                    />
                  </RadioGroup>
                </FormControl>
              </Card>
            </Box>
            
            {filterType === 'month' ? (
              <>
                <Box sx={{ flex: '1 1 200px', minWidth: '180px' }}>
                  <Card sx={{ p: 3, backgroundColor: '#f0f9ff', border: '1px solid #bae6fd' }}>
                    <FormControl fullWidth>
                      <InputLabel>Month</InputLabel>
                      <Select
                        value={selectedMonth}
                        label="Month"
                        onChange={(e) => setSelectedMonth(e.target.value)}
                        sx={{ 
                          borderRadius: 2,
                          backgroundColor: 'white',
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': {
                              borderColor: '#0ea5e9',
                            },
                          },
                        }}
                      >
                        <MenuItem value="">All Months</MenuItem>
                        {months.map((month) => (
                          <MenuItem key={month.value} value={month.value}>
                            {month.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Card>
                </Box>
                <Box sx={{ flex: '1 1 200px', minWidth: '180px' }}>
                  <Card sx={{ p: 3, backgroundColor: '#f0f9ff', border: '1px solid #bae6fd' }}>
                    <FormControl fullWidth>
                      <InputLabel>Year</InputLabel>
                      <Select
                        value={selectedYear}
                        label="Year"
                        onChange={(e) => setSelectedYear(e.target.value)}
                        sx={{ 
                          borderRadius: 2,
                          backgroundColor: 'white',
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': {
                              borderColor: '#0ea5e9',
                            },
                          },
                        }}
                      >
                        <MenuItem value="">All Years</MenuItem>
                        {years.map((year) => (
                          <MenuItem key={year} value={year.toString()}>
                            {year}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Card>
                </Box>
              </>
            ) : (
              <>
                <Box sx={{ flex: '1 1 200px', minWidth: '180px' }}>
                  <Card sx={{ p: 3, backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                    <TextField
                      fullWidth
                      label="Start Date"
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{ 
                        borderRadius: 2,
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'white',
                          '&:hover fieldset': {
                            borderColor: '#10b981',
                          },
                        },
                      }}
                    />
                  </Card>
                </Box>
                <Box sx={{ flex: '1 1 200px', minWidth: '180px' }}>
                  <Card sx={{ p: 3, backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                    <TextField
                      fullWidth
                      label="End Date"
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{ 
                        borderRadius: 2,
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'white',
                          '&:hover fieldset': {
                            borderColor: '#10b981',
                          },
                        },
                      }}
                    />
                  </Card>
                </Box>
              </>
            )}
          </Box>
          
          {filterType === 'date' && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Button
                variant="contained"
                onClick={handleApplyDateFilter}
                disabled={!startDate || !endDate}
                sx={{ mr: 2 }}
              >
                Apply Filter
              </Button>
            </Box>
          )}
          
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Button
              variant="outlined"
              onClick={handleClearFilters}
            >
              Clear Filters
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Professional Report Content */}
      <div ref={reportRef}>
        {/* Report Header */}
        <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2, '@media print': { boxShadow: 'none', border: '1px solid #ddd' } }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
              PAYROLL REPORT
            </Typography>
            <Typography variant="h6" sx={{ color: 'text.secondary', mb: 2 }}>
              {filterType === 'month' 
                ? `${months.find(m => m.value === selectedMonth)?.label || ''} ${selectedYear}`
                : `${startDate ? formatDate(startDate) : 'N/A'} - ${endDate ? formatDate(endDate) : 'N/A'}`
              }
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Generated on {formatDate(new Date().toISOString())}
            </Typography>
          </Box>
          
          {filteredPayrolls.length === 0 && (
            <Alert severity="info" sx={{ mb: 3 }}>
              No payroll data found for the selected period. Please adjust your filters.
            </Alert>
          )}
        </Paper>

        {/* Enhanced Summary Cards */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
          <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
            <Card 
              elevation={0} 
              sx={{ 
                borderRadius: 3, 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
                color: 'white',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 40px rgba(102, 126, 234, 0.4)',
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="h3" sx={{ fontWeight: 700, mb: 1, fontSize: '2.5rem' }}>
                      {totalEmployees}
                    </Typography>
                    <Typography variant="body1" sx={{ opacity: 0.9, fontWeight: 500 }}>
                      Total Employees
                    </Typography>
                  </Box>
                  <Avatar sx={{ width: 56, height: 56, backgroundColor: 'rgba(255, 255, 255, 0.2)' }}>
                    <PeopleIcon sx={{ fontSize: 32 }} />
                  </Avatar>
                </Stack>
              </CardContent>
            </Card>
          </Box>
          
          <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
            <Card 
              elevation={0} 
              sx={{ 
                borderRadius: 3, 
                background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', 
                color: 'white',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                boxShadow: '0 8px 32px rgba(17, 153, 142, 0.3)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 40px rgba(17, 153, 142, 0.4)',
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="h3" sx={{ fontWeight: 700, mb: 1, fontSize: '2.5rem' }}>
                      {formatCurrency(totalGrossPay)}
                    </Typography>
                    <Typography variant="body1" sx={{ opacity: 0.9, fontWeight: 500 }}>
                      Total Gross Pay
                    </Typography>
                  </Box>
                  <Avatar sx={{ width: 56, height: 56, backgroundColor: 'rgba(255, 255, 255, 0.2)' }}>
                    <MoneyIcon sx={{ fontSize: 32 }} />
                  </Avatar>
                </Stack>
              </CardContent>
            </Card>
          </Box>
          
          <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
            <Card 
              elevation={0} 
              sx={{ 
                borderRadius: 3, 
                background: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)', 
                color: 'white',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                boxShadow: '0 8px 32px rgba(255, 154, 158, 0.3)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 40px rgba(255, 154, 158, 0.4)',
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="h3" sx={{ fontWeight: 700, mb: 1, fontSize: '2.5rem' }}>
                      {formatCurrency(totalDeductions)}
                    </Typography>
                    <Typography variant="body1" sx={{ opacity: 0.9, fontWeight: 500 }}>
                      Total Deductions
                    </Typography>
                  </Box>
                  <Avatar sx={{ width: 56, height: 56, backgroundColor: 'rgba(255, 255, 255, 0.2)' }}>
                    <TrendingUpIcon sx={{ fontSize: 32 }} />
                  </Avatar>
                </Stack>
              </CardContent>
            </Card>
          </Box>
          
          <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
            <Card 
              elevation={0} 
              sx={{ 
                borderRadius: 3, 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
                color: 'white',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 40px rgba(102, 126, 234, 0.4)',
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="h3" sx={{ fontWeight: 700, mb: 1, fontSize: '2.5rem' }}>
                      {formatCurrency(totalNetPay)}
                    </Typography>
                    <Typography variant="body1" sx={{ opacity: 0.9, fontWeight: 500 }}>
                      Total Net Pay
                    </Typography>
                  </Box>
                  <Avatar sx={{ width: 56, height: 56, backgroundColor: 'rgba(255, 255, 255, 0.2)' }}>
                    <MoneyIcon sx={{ fontSize: 32 }} />
                  </Avatar>
                </Stack>
              </CardContent>
            </Card>
          </Box>
        </Box>

        {/* Payroll Records Grouped by Employee */}
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Payroll Records by Employee
          </Typography>
          {Object.entries(groupPayrollsByEmployee()).length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              No payroll records found.
            </Typography>
          ) : (
            Object.entries(groupPayrollsByEmployee()).map(([employeeId, { employee, payrolls }]) => (
              <Accordion
                key={employeeId}
                expanded={expandedEmployees.has(employeeId)}
                onChange={() => handleAccordionChange(employeeId)}
                sx={{ mb: 1 }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                      <PersonIcon />
                    </Avatar>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {employee.first_name} {employee.last_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {employee.department} • {employee.position}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, mr: 2 }}>
                      <Badge badgeContent={payrolls.length} color="primary">
                        <Typography variant="body2" color="text.secondary">
                          Records
                        </Typography>
                      </Badge>
                      <Typography variant="body2" color="primary" fontWeight="bold">
                        Total: {formatCurrency(payrolls.reduce((sum, p) => sum + p.net_pay, 0))}
                      </Typography>
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Divider sx={{ mb: 2 }} />
                  {payrolls.map((payroll, index) => (
                    <Card key={payroll.id} variant="outlined" sx={{ mb: index < payrolls.length - 1 ? 2 : 0 }}>
                      <CardHeader
                        title={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle2" fontWeight="bold">
                              Pay Period: {payroll.pay_period_start ? formatDate(payroll.pay_period_start) : 'N/A'} - {payroll.pay_period_end ? formatDate(payroll.pay_period_end) : 'N/A'}
                            </Typography>
                            <Chip
                              label={payroll.payment_status}
                              color={payroll.payment_status === 'paid' ? 'success' : payroll.payment_status === 'pending' ? 'warning' : 'error'}
                              size="small"
                            />
                          </Box>
                        }
                        subheader={
                          <Typography variant="body2" color="text.secondary">
                            Processed: {payroll.payment_date ? formatDate(payroll.payment_date) : 'N/A'}
                          </Typography>
                        }
                      />
                      <Box sx={{ px: 2, pb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            Gross Pay:
                          </Typography>
                          <Typography variant="body2" fontWeight="bold" color="success.main">
                            {formatCurrency(payroll.gross_pay)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            Deductions:
                          </Typography>
                          <Typography variant="body2" fontWeight="bold" color="error.main">
                            {formatCurrency(payroll.total_deductions)}
                          </Typography>
                        </Box>
                        <Divider sx={{ my: 1 }} />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body1" fontWeight="bold">
                            Net Pay:
                          </Typography>
                          <Typography variant="body1" fontWeight="bold" color="primary.main" fontSize="1.1rem">
                            {formatCurrency(payroll.net_pay)}
                          </Typography>
                        </Box>
                      </Box>
                    </Card>
                  ))}
                </AccordionDetails>
              </Accordion>
            ))
          )}
        </Paper>
      </div>
    </Container>
  );
};

export default ReportsPage;